// Every word of the first run (sign-up → onboarding → /intro → the first
// Today tour) in four languages — see src/utils/firstRunLang.js for why.
//
// Keys are flat strings; `{name}` is filled in by `t(key, { name })`.
// A missing key falls back to English, and firstRunCopy.test.js fails the
// build when a language lacks one, so a new line is written four times.
// The VALUES stored by the wizard (goal 'Speaking', topic 'Movies', a CEFR
// level) never change with the language — only what is shown.
//
// Tone: «sən», short sentences, say what the app gives — never name a fear.

const en = {
  'lang.pick': 'Language',

  'reg.title': 'Start {speaking}',
  'reg.sub': 'Speak every day — with a real partner or with AInur.',
  'reg.invite': 'You are joining through a teacher invitation — you will be connected right after you sign up.',
  'reg.student': 'I am a Student',
  'reg.studentSub': 'Practice speaking',
  'reg.teacher': 'I am a Teacher',
  'reg.teacherSub': 'Track my students',
  'reg.chooseRole': 'Choose your role to continue',
  'reg.google': 'Sign up with Google',
  'reg.or': 'or',
  'reg.name': 'Full Name',
  'reg.namePh': 'Your name',
  'reg.email': 'Email',
  'reg.password': 'Password',
  'reg.passwordPh': 'At least 6 characters',
  'reg.submit': 'Get Started',
  'reg.creating': 'Creating account...',
  'reg.haveAccount': 'Already have an account?',
  'reg.signIn': 'Sign in',
  'reg.googleError': 'Google sign-up did not work: {msg}',

  'ob.back': 'Back',
  'ob.continue': 'Continue',
  'ob.getStarted': 'Get started',
  'ob.imIn': 'I’m in',
  'ob.save': 'Save',
  'ob.saving': 'Saving…',
  'ob.confirm': 'Confirm and plan my week',
  'ob.reviewAll': 'Review all my answers',
  'ob.continueTarget': 'Continue — pick your weekly target',
  'ob.errSave': 'Your answers were not saved. Check your connection and try again.',
  'ob.edit': 'Edit',

  'ob.welcome.title': 'A place to speak English, every week',
  'ob.welcome.sub': 'You practise with real partners at times that suit you, and every conversation turns into a report of what to work on next.',
  'ob.welcome.p1': 'Real conversations with learners at your level',
  'ob.welcome.p2': 'A weekly plan built around your free time',
  'ob.welcome.p3': 'Feedback after each practice',
  'ob.welcome.note': 'A few questions help us find the right partners for you. It takes about two minutes.',

  'ob.goal.title': 'What brings you here?',
  'ob.goal.sub': 'Pick the one that matters most right now.',
  'goal.Speaking': 'Speak with confidence',
  'goal.Speaking.hint': 'Everyday conversation without freezing',
  'goal.Business': 'English for work',
  'goal.Business.hint': 'Meetings, calls, interviews',
  'goal.Exam': 'Pass an exam',
  'goal.Exam.hint': 'IELTS, TOEFL, school exams',
  'goal.Travel': 'Travel and living abroad',
  'goal.Travel.hint': 'Getting around, meeting people',
  'goal.Fun': 'For myself',
  'goal.Fun.hint': 'I enjoy the language',

  'ob.level.title': 'How do you speak today?',
  'ob.level.sub': 'Your level decides who you practise with. You can change it later.',
  'ob.level.test': 'Not sure? Take the 3-minute level test',
  'level.A1': 'Beginner',
  'level.A1.hint': 'I know some words and phrases',
  'level.A2': 'Elementary',
  'level.A2.hint': 'I can talk about simple, familiar things',
  'level.B1': 'Intermediate',
  'level.B1.hint': 'I can keep a conversation going, with pauses',
  'level.B2': 'Upper-Intermediate',
  'level.B2.hint': 'I speak fairly freely on most topics',
  'level.C1': 'Advanced',
  'level.C1.hint': 'I speak fluently, I want polish',
  'level.C2': 'Proficient',
  'level.C2.hint': 'Near-native',

  'ob.age.title': 'How old are you?',
  'ob.age.sub': 'We use this to match you with suitable partners. Only the SpeakLab team sees it.',
  'age.under18': 'Under 18',
  'age.35+': '35 or older',

  'ob.place.title': 'Where are you?',
  'ob.place.sub': 'Your partners may be in another country, so we plan every practice in your own time.',
  'ob.place.now': 'Your time now:',
  'ob.place.change': 'Change',
  'ob.place.done': 'Done',
  'ob.place.note': 'If the time above is not your clock right now, tap Change.',
  'country.Azerbaijan': 'Azerbaijan',
  'country.Türkiye': 'Türkiye',
  'country.Georgia': 'Georgia',
  'country.Russia': 'Russia',
  'country.Kazakhstan': 'Kazakhstan',
  'country.Uzbekistan': 'Uzbekistan',
  'country.Germany': 'Germany',
  'country.United Kingdom': 'United Kingdom',
  'country.United States': 'United States',
  'country.Other': 'Other',

  'ob.charter.title': 'How practice works here',
  'ob.charter.sub': 'Every practice at SpeakLab is planned ahead, with a real partner.',
  'ob.charter.c1': 'You tell us the hours you can really attend, and how many practices a week you want.',
  'ob.charter.c2': 'Every Sunday evening, next week’s practices arrive. Confirm each with one tap — it is booked when your partner says yes too.',
  'ob.charter.c3': 'Your partner keeps that hour for you, and you keep it for them. Plans change? Cancel at least 2 hours ahead, so the time can go to someone else.',
  'ob.charter.c4': 'Anyone you have practised with is one message or call away, any time.',

  'ob.phone.title': 'Your WhatsApp number',
  'ob.phone.sub': 'Only the SpeakLab team sees it — we write when a practice needs sorting out. Partners talk in the app chat.',
  'ob.phone.ok': 'We will write to {n}',
  'ob.phone.short': 'This does not look like a full number yet.',
  'ob.phone.hint': 'The number you use on WhatsApp.',
  'ob.phone.required': 'Add your WhatsApp number to save.',
  'ob.notify.title': 'Know when your partner is found',
  'ob.notify.sub': 'We send one message when a practice is booked for you, and one before it starts.',
  'ob.notify.on': 'Turn on notifications',
  'ob.notify.later': 'Not now',
  'ob.notify.done': 'Notifications are on.',
  'ob.notify.blocked': 'Notifications are off in this browser — you can turn them on in its settings later.',

  'ob.avail.title': 'When are you usually free?',
  'ob.avail.sub': 'Tap the hours you are free. Times: {city}.',
  'ob.avail.clear': 'Clear',
  'ob.avail.popular': 'Popular — more people are free then',
  'ob.avail.editing': 'Changes count from your next weekly plan (Sunday). Practices you have already confirmed stay as they are.',
  'ob.avail.none': 'Choose at least one free hour.',
  'ob.avail.summary.one': '{h} h free across {d} day',
  'ob.avail.summary.other': '{h} h free across {d} days',
  'ob.avail.maybe': '{n} h maybe',
  'ob.avail.never': '{n} h never',
  'preset.weekday-evenings': 'Weekday evenings',
  'preset.weekend-days': 'Weekend daytime',
  'preset.late': 'Late evenings',
  'mode.free': 'Free',
  'mode.free.hint': 'This is enough',
  'mode.maybe': 'Maybe',
  'mode.maybe.hint': 'Optional',
  'mode.never': 'Never',
  'mode.never.hint': 'Optional',
  'ob.avail.howMaybe': 'Maybe: if no other time fits.',
  'ob.avail.howNever': 'Never: we will not suggest these hours.',
  'ob.avail.backFree': 'Back to Free',

  'ob.target.title': 'How many practices a week will you keep?',
  'ob.target.sub': 'Being free is not the same as committing. Choose the number you can really keep — every booked practice has a partner waiting for you.',
  'target.1': 'Once a week',
  'target.2': 'Twice a week',
  'target.3': 'Three times',
  'target.4': 'Four or more',
  'ob.target.limit.one': 'You are free on {d} day, so you can plan up to {d} practice a week.',
  'ob.target.limit.other': 'You are free on {d} days, so you can plan up to {d} practices a week.',
  'ob.target.addMore': 'Add more free time',

  'topic.Technology': 'Technology',
  'topic.Movies': 'Movies',
  'topic.Music': 'Music',
  'topic.Travel': 'Travel',
  'topic.Business': 'Business',
  'topic.Sport': 'Sport',
  'topic.Food': 'Food',
  'topic.Books': 'Books',
  'topic.Science': 'Science',
  'topic.Psychology': 'Psychology',
  'topic.Career': 'Career',
  'topic.General': 'General',

  'ob.summary.title': 'Your practice profile',
  'ob.summary.sub': 'Check it once. The SpeakLab team uses it to plan your partners and practice times.',
  'row.goal': 'Goal',
  'row.level': 'Level',
  'row.age': 'Age',
  'row.location': 'Location',
  'row.whatsapp': 'WhatsApp',
  'row.free': 'Free time',
  'row.perWeek': 'Per week',
  'row.maybe': 'Maybe: {n} h',
  'row.never': 'Never: {n} h',
  'row.perWeekValue': '{n}',
  'day.0': 'Sun',
  'day.1': 'Mon',
  'day.2': 'Tue',
  'day.3': 'Wed',
  'day.4': 'Thu',
  'day.5': 'Fri',
  'day.6': 'Sat',

  'intro.top': 'Meet the team',
  'intro.back': 'Back to the app',
  'intro.doneTitle': 'You have met the team',
  'intro.doneSub': 'Questions any time? We are on WhatsApp.',
  'intro.title': 'Say hi to the SpeakLab team',
  'intro.subMust': 'Your first step: write to us on WhatsApp. Tell us your goals, ask anything, and we help you plan your practice week. Then the whole app is yours, calls included.',
  'intro.sub': 'Write to us on WhatsApp: ask anything, tell us your goals, and we help you plan your practice week. The whole app is open to you right now.',
  'intro.wa': 'Write to us on WhatsApp',
  'intro.waSub': 'Your name and level are already in the message',
  'intro.continue': 'Continue to the app',
  'intro.waText': 'Hi! I’m {name}{level}. I’ve just joined SpeakLab.',

  'tour.badge': 'Guided setup',
  'tour.skip': 'Skip',
  'tour.back': 'Back',
  'tour.next': 'Next',
  'tour.finish': 'Finish',
  'tour.next.title': 'Your practices, planned',
  'tour.next.text': 'Every week we plan practices with real partners for you. On Plan, one tap confirms each one.',
  'tour.topic.title': 'Today’s topic',
  'tour.topic.text': 'Words, questions and a short listening for today’s conversation. Open it before you talk.',
  'tour.ai.title': 'AInur, any time',
  'tour.ai.text': 'Warm up by speaking with AInur whenever you like.',
  'tour.lab.title': 'Your progress',
  'tour.lab.text': 'After each practice, Lab shows your new words, your mistakes and how you grow.',
  'tour.board.title': 'Leaderboard',
  'tour.board.text': 'See who practised most this week. Every practice moves you up.',
  'tour.partners.title': 'Partners',
  'tour.partners.text': 'Message the people you practised with and call them again.',
  // The install gate (components/InstallGate.jsx) — the browser steps stay English.
  'gate.play.title': 'Get the SpeakLab app',
  'gate.play.sub': 'It is on Google Play. Your partner’s calls ring even with the screen off, and practice reminders reach you.',
  'gate.perk.calls': 'Calls ring',
  'gate.perk.reminders': 'Reminders',
  'gate.perk.updates': 'Updates itself',
  'gate.perk.full': 'Full screen',
  'gate.play.button': 'Get it on Google Play',
  'gate.play.after': 'Then sign in the same way as here — your account and practices are already there.',
  'gate.skip': 'Continue in the browser',
  'gate.a2hs.title': 'Add SpeakLab to your home screen',
  'gate.inapp.title': 'Open SpeakLab in your browser',
  'gate.a2hs.sub': 'It opens full screen like an app, and your partner’s calls and practice reminders reach you.',
  'gate.install': 'Install SpeakLab',
  'gate.in': 'In',
  'gate.byhand': 'Or by hand in',
  'gate.other': 'Other browser',
  'gate.copy': 'Copy the link',
  'gate.copied': 'Copied',
  'gate.after': 'Then open SpeakLab from your home screen and allow notifications.',
  'gate.help': 'Need help? Write to us',
};

const az = {
  'lang.pick': 'Dil',

  'reg.title': '{Danışmağa} başla',
  'reg.sub': 'Hər gün danış — real partnyorla və ya AInur ilə.',
  'reg.invite': 'Müəllimin dəvəti ilə qoşulursan — qeydiyyatdan dərhal sonra ona bağlanacaqsan.',
  'reg.student': 'Mən tələbəyəm',
  'reg.studentSub': 'Danışıq məşqi',
  'reg.teacher': 'Mən müəlliməm',
  'reg.teacherSub': 'Tələbələrimi izləyim',
  'reg.chooseRole': 'Davam etmək üçün rolunu seç',
  'reg.google': 'Google ilə qeydiyyat',
  'reg.or': 'və ya',
  'reg.name': 'Ad və soyad',
  'reg.namePh': 'Adın',
  'reg.email': 'E-poçt',
  'reg.password': 'Şifrə',
  'reg.passwordPh': 'Ən azı 6 simvol',
  'reg.submit': 'Başla',
  'reg.creating': 'Hesab yaradılır...',
  'reg.haveAccount': 'Artıq hesabın var?',
  'reg.signIn': 'Daxil ol',
  'reg.googleError': 'Google ilə qeydiyyat alınmadı: {msg}',

  'ob.back': 'Geri',
  'ob.continue': 'Davam et',
  'ob.getStarted': 'Başlayaq',
  'ob.imIn': 'Razıyam',
  'ob.save': 'Yadda saxla',
  'ob.saving': 'Saxlanılır…',
  'ob.confirm': 'Təsdiqlə və həftəmi planla',
  'ob.reviewAll': 'Bütün cavablarıma bax',
  'ob.continueTarget': 'Davam et — həftəlik hədəfini seç',
  'ob.errSave': 'Cavabların saxlanılmadı. İnterneti yoxla və yenidən cəhd et.',
  'ob.edit': 'Dəyiş',

  'ob.welcome.title': 'Hər həftə ingiliscə danışmaq üçün yer',
  'ob.welcome.sub': 'Sənə uyğun saatlarda real partnyorlarla məşq edirsən, hər söhbətdən sonra nəyin üzərində işləməli olduğunu göstərən hesabat alırsan.',
  'ob.welcome.p1': 'Öz səviyyəndə olanlarla real söhbətlər',
  'ob.welcome.p2': 'Boş vaxtına görə qurulan həftəlik plan',
  'ob.welcome.p3': 'Hər məşqdən sonra rəy',
  'ob.welcome.note': 'Bir neçə sual sənə uyğun partnyor tapmağa kömək edir. Təxminən iki dəqiqə çəkir.',

  'ob.goal.title': 'Səni bura nə gətirdi?',
  'ob.goal.sub': 'İndi sənin üçün ən vacib olanı seç.',
  'goal.Speaking': 'Rahat danışmaq',
  'goal.Speaking.hint': 'Gündəlik söhbətdə donub qalmadan',
  'goal.Business': 'İş üçün ingilis dili',
  'goal.Business.hint': 'Görüşlər, zənglər, müsahibələr',
  'goal.Exam': 'İmtahandan keçmək',
  'goal.Exam.hint': 'IELTS, TOEFL, məktəb imtahanları',
  'goal.Travel': 'Səyahət və xaricdə yaşamaq',
  'goal.Travel.hint': 'Yol tapmaq, insanlarla tanış olmaq',
  'goal.Fun': 'Özüm üçün',
  'goal.Fun.hint': 'Dili sevirəm',

  'ob.level.title': 'Bu gün necə danışırsan?',
  'ob.level.sub': 'Kiminlə məşq edəcəyini səviyyən müəyyən edir. Sonra dəyişə bilərsən.',
  'ob.level.test': 'Əmin deyilsən? 3 dəqiqəlik səviyyə testini keç',
  'level.A1': 'Başlanğıc',
  'level.A1.hint': 'Bir neçə söz və ifadə bilirəm',
  'level.A2': 'Elementar',
  'level.A2.hint': 'Sadə, tanış mövzularda danışa bilirəm',
  'level.B1': 'Orta',
  'level.B1.hint': 'Fasilələrlə də olsa söhbəti davam etdirə bilirəm',
  'level.B2': 'Ortadan yuxarı',
  'level.B2.hint': 'Əksər mövzularda kifayət qədər sərbəst danışıram',
  'level.C1': 'İrəli',
  'level.C1.hint': 'Axıcı danışıram, cilalamaq istəyirəm',
  'level.C2': 'Peşəkar',
  'level.C2.hint': 'Ana dilinə yaxın',

  'ob.age.title': 'Neçə yaşın var?',
  'ob.age.sub': 'Bu, sənə uyğun partnyor seçmək üçündür. Yalnız SpeakLab komandası görür.',
  'age.under18': '18-dən kiçik',
  'age.35+': '35 və yuxarı',

  'ob.place.title': 'Haradasan?',
  'ob.place.sub': 'Partnyorların başqa ölkədə ola bilər, ona görə hər məşqi sənin öz saatınla planlayırıq.',
  'ob.place.now': 'İndi səndə saat:',
  'ob.place.change': 'Dəyiş',
  'ob.place.done': 'Hazır',
  'ob.place.note': 'Yuxarıdakı saat indiki saatın deyilsə, «Dəyiş»ə toxun.',
  'country.Azerbaijan': 'Azərbaycan',
  'country.Türkiye': 'Türkiyə',
  'country.Georgia': 'Gürcüstan',
  'country.Russia': 'Rusiya',
  'country.Kazakhstan': 'Qazaxıstan',
  'country.Uzbekistan': 'Özbəkistan',
  'country.Germany': 'Almaniya',
  'country.United Kingdom': 'Böyük Britaniya',
  'country.United States': 'ABŞ',
  'country.Other': 'Digər',

  'ob.charter.title': 'Burada məşq necə işləyir',
  'ob.charter.sub': 'SpeakLab-da hər məşq əvvəlcədən, real partnyorla planlanır.',
  'ob.charter.c1': 'Həqiqətən gələ biləcəyin saatları və həftədə neçə məşq istədiyini bizə deyirsən.',
  'ob.charter.c2': 'Hər bazar axşamı gələn həftənin məşqləri gəlir. Hər birini bir toxunuşla təsdiqlə — partnyorun da «hə» deyəndə rezerv olunur.',
  'ob.charter.c3': 'Partnyorun o saatı sənin üçün saxlayır, sən də onun üçün. Plan dəyişdi? Ən azı 2 saat əvvəl ləğv et ki, vaxt başqasına keçsin.',
  'ob.charter.c4': 'Məşq etdiyin hər kəslə istənilən vaxt yazışa və ya zəng edə bilərsən.',

  'ob.phone.title': 'WhatsApp nömrən',
  'ob.phone.sub': 'Yalnız SpeakLab komandası görür — məşqlə bağlı nəsə həll etmək lazım olanda yazırıq. Partnyorlar tətbiqdaxili çatda danışır.',
  'ob.phone.ok': '{n} nömrəsinə yazacağıq',
  'ob.phone.short': 'Bu hələ tam nömrəyə oxşamır.',
  'ob.phone.hint': 'WhatsApp-da istifadə etdiyin nömrə.',
  'ob.phone.required': 'Yadda saxlamaq üçün WhatsApp nömrəni əlavə et.',
  'ob.notify.title': 'Partnyor tapılanda xəbərin olsun',
  'ob.notify.sub': 'Sənə məşq bron olunanda bir mesaj, başlamazdan əvvəl də bir mesaj göndəririk.',
  'ob.notify.on': 'Bildirişləri aç',
  'ob.notify.later': 'Sonra',
  'ob.notify.done': 'Bildirişlər açıqdır.',
  'ob.notify.blocked': 'Bu brauzerdə bildirişlər bağlıdır — sonra onun ayarlarından aça bilərsən.',

  'ob.avail.title': 'Adətən nə vaxt boşsan?',
  'ob.avail.sub': 'Boş olduğun saatlara toxun. Vaxt: {city}.',
  'ob.avail.clear': 'Təmizlə',
  'ob.avail.popular': 'Populyar — bu saatlarda daha çox adam boşdur',
  'ob.avail.editing': 'Dəyişikliklər növbəti həftəlik plandan (bazar) qüvvəyə minir. Artıq təsdiqlədiyin məşqlər olduğu kimi qalır.',
  'ob.avail.none': 'Ən azı bir boş saat seç.',
  'ob.avail.summary.one': '{d} gündə {h} saat boş',
  'ob.avail.summary.other': '{d} gündə {h} saat boş',
  'ob.avail.maybe': '{n} saat «bəlkə»',
  'ob.avail.never': '{n} saat «heç vaxt»',
  'preset.weekday-evenings': 'İş günləri axşam',
  'preset.weekend-days': 'Həftəsonu gündüz',
  'preset.late': 'Gec axşam',
  'mode.free': 'Boş',
  'mode.free.hint': 'Bu kifayətdir',
  'mode.maybe': 'Bəlkə',
  'mode.maybe.hint': 'İstəyə bağlı',
  'mode.never': 'Heç vaxt',
  'mode.never.hint': 'İstəyə bağlı',
  'ob.avail.howMaybe': 'Bəlkə: başqa vaxt olmasa.',
  'ob.avail.howNever': 'Heç vaxt: bu saatları təklif etmirik.',
  'ob.avail.backFree': '«Boş»a qayıt',

  'ob.target.title': 'Həftədə neçə məşqə gələcəksən?',
  'ob.target.sub': 'Boş olmaq söz vermək demək deyil. Həqiqətən gələ biləcəyin sayı seç — hər təsdiqlənmiş məşqdə səni bir partnyor gözləyir.',
  'target.1': 'Həftədə bir dəfə',
  'target.2': 'Həftədə iki dəfə',
  'target.3': 'Üç dəfə',
  'target.4': 'Dörd və ya çox',
  'ob.target.limit.one': '{d} gün boşsan, ona görə həftədə ən çox {d} məşq planlaya bilərsən.',
  'ob.target.limit.other': '{d} gün boşsan, ona görə həftədə ən çox {d} məşq planlaya bilərsən.',
  'ob.target.addMore': 'Daha çox boş vaxt əlavə et',

  'topic.Technology': 'Texnologiya',
  'topic.Movies': 'Filmlər',
  'topic.Music': 'Musiqi',
  'topic.Travel': 'Səyahət',
  'topic.Business': 'Biznes',
  'topic.Sport': 'İdman',
  'topic.Food': 'Yemək',
  'topic.Books': 'Kitablar',
  'topic.Science': 'Elm',
  'topic.Psychology': 'Psixologiya',
  'topic.Career': 'Karyera',
  'topic.General': 'Ümumi',

  'ob.summary.title': 'Məşq profilin',
  'ob.summary.sub': 'Bir dəfə yoxla. SpeakLab komandası partnyorlarını və məşq vaxtlarını buna görə planlayır.',
  'row.goal': 'Məqsəd',
  'row.level': 'Səviyyə',
  'row.age': 'Yaş',
  'row.location': 'Yer',
  'row.whatsapp': 'WhatsApp',
  'row.free': 'Boş vaxt',
  'row.perWeek': 'Həftədə',
  'row.maybe': 'Bəlkə: {n} saat',
  'row.never': 'Heç vaxt: {n} saat',
  'row.perWeekValue': '{n} məşq',
  'day.0': 'B.',
  'day.1': 'B.e.',
  'day.2': 'Ç.a.',
  'day.3': 'Ç.',
  'day.4': 'C.a.',
  'day.5': 'C.',
  'day.6': 'Ş.',

  'intro.top': 'Komanda ilə tanış ol',
  'intro.back': 'Tətbiqə qayıt',
  'intro.doneTitle': 'Komanda ilə tanış oldun',
  'intro.doneSub': 'Sualın var? WhatsApp-dayıq.',
  'intro.title': 'SpeakLab komandasına salam yaz',
  'intro.subMust': 'İlk addımın: bizə WhatsApp-da yaz. Məqsədlərini de, istədiyini soruş — məşq həftəni planlamağa kömək edək. Sonra bütün tətbiq, zənglər də daxil, sənindir.',
  'intro.sub': 'Bizə WhatsApp-da yaz: istədiyini soruş, məqsədlərini de — məşq həftəni planlamağa kömək edək. Bütün tətbiq artıq sənə açıqdır.',
  'intro.wa': 'WhatsApp-da bizə yaz',
  'intro.waSub': 'Adın və səviyyən mesajda artıq yazılıb',
  'intro.continue': 'Tətbiqə keç',
  'intro.waText': 'Salam! Mən {name}{level}. SpeakLab-a yeni qoşulmuşam.',

  'tour.badge': 'Qısa bələdçi',
  'tour.skip': 'Keç',
  'tour.back': 'Geri',
  'tour.next': 'Növbəti',
  'tour.finish': 'Bitir',
  'tour.next.title': 'Məşqlərin planlanır',
  'tour.next.text': 'Hər həftə sənin üçün real partnyorlarla məşqlər planlayırıq. Plan bölməsində hər birini bir toxunuşla təsdiqlə.',
  'tour.topic.title': 'Bugünkü mövzu',
  'tour.topic.text': 'Bugünkü söhbət üçün sözlər, suallar və qısa dinləmə. Danışmazdan əvvəl aç.',
  'tour.ai.title': 'AInur, istənilən vaxt',
  'tour.ai.text': 'İstədiyin vaxt AInur ilə danışıb isin.',
  'tour.lab.title': 'İrəliləyişin',
  'tour.lab.text': 'Hər məşqdən sonra Lab yeni sözlərini, səhvlərini və necə irəlilədiyini göstərir.',
  'tour.board.title': 'Liderlər cədvəli',
  'tour.board.text': 'Bu həftə kimin ən çox məşq etdiyini gör. Hər məşq səni yuxarı qaldırır.',
  'tour.partners.title': 'Partnyorlar',
  'tour.partners.text': 'Məşq etdiyin insanlara yaz və onlara yenidən zəng et.',
  // The install gate (components/InstallGate.jsx) — the browser steps stay English.
  'gate.play.title': 'SpeakLab tətbiqini yüklə',
  'gate.play.sub': 'Tətbiq Google Play-dədir. Partnyorunun zəngi ekran sönülü olanda da çalır, məşq xatırlatmaları sənə çatır.',
  'gate.perk.calls': 'Zənglər çalır',
  'gate.perk.reminders': 'Xatırlatmalar',
  'gate.perk.updates': 'Özü yenilənir',
  'gate.perk.full': 'Tam ekran',
  'gate.play.button': 'Google Play-dən yüklə',
  'gate.play.after': 'Sonra burada olduğu kimi daxil ol — hesabın və məşqlərin artıq oradadır.',
  'gate.skip': 'Brauzerdə davam et',
  'gate.a2hs.title': 'SpeakLab-ı ana ekrana əlavə et',
  'gate.inapp.title': 'SpeakLab-ı brauzerdə aç',
  'gate.a2hs.sub': 'Tətbiq kimi tam ekranda açılır, partnyorunun zəngləri və məşq xatırlatmaları sənə çatır.',
  'gate.install': 'SpeakLab-ı quraşdır',
  'gate.in': 'Brauzer:',
  'gate.byhand': 'Və ya əl ilə:',
  'gate.other': 'Başqa brauzer',
  'gate.copy': 'Linki kopyala',
  'gate.copied': 'Kopyalandı',
  'gate.after': 'Sonra SpeakLab-ı ana ekrandan aç və bildirişlərə icazə ver.',
  'gate.help': 'Kömək lazımdır? Bizə yaz',
};

const tr = {
  'lang.pick': 'Dil',

  'reg.title': '{Konuşmaya} başla',
  'reg.sub': 'Her gün konuş — gerçek bir partnerle ya da AInur ile.',
  'reg.invite': 'Bir öğretmen davetiyle katılıyorsun — kaydolduktan hemen sonra ona bağlanacaksın.',
  'reg.student': 'Öğrenciyim',
  'reg.studentSub': 'Konuşma pratiği',
  'reg.teacher': 'Öğretmenim',
  'reg.teacherSub': 'Öğrencilerimi takip edeyim',
  'reg.chooseRole': 'Devam etmek için rolünü seç',
  'reg.google': 'Google ile kaydol',
  'reg.or': 'veya',
  'reg.name': 'Ad soyad',
  'reg.namePh': 'Adın',
  'reg.email': 'E-posta',
  'reg.password': 'Şifre',
  'reg.passwordPh': 'En az 6 karakter',
  'reg.submit': 'Başla',
  'reg.creating': 'Hesap oluşturuluyor...',
  'reg.haveAccount': 'Zaten hesabın var mı?',
  'reg.signIn': 'Giriş yap',
  'reg.googleError': 'Google ile kayıt olmadı: {msg}',

  'ob.back': 'Geri',
  'ob.continue': 'Devam',
  'ob.getStarted': 'Başlayalım',
  'ob.imIn': 'Varım',
  'ob.save': 'Kaydet',
  'ob.saving': 'Kaydediliyor…',
  'ob.confirm': 'Onayla ve haftamı planla',
  'ob.reviewAll': 'Tüm cevaplarıma bak',
  'ob.continueTarget': 'Devam — haftalık hedefini seç',
  'ob.errSave': 'Cevapların kaydedilmedi. Bağlantını kontrol edip tekrar dene.',
  'ob.edit': 'Değiştir',

  'ob.welcome.title': 'Her hafta İngilizce konuşabileceğin bir yer',
  'ob.welcome.sub': 'Sana uygun saatlerde gerçek partnerlerle pratik yaparsın; her sohbet, neye çalışman gerektiğini gösteren bir rapora dönüşür.',
  'ob.welcome.p1': 'Senin seviyendeki kişilerle gerçek sohbetler',
  'ob.welcome.p2': 'Boş zamanına göre kurulan haftalık plan',
  'ob.welcome.p3': 'Her pratikten sonra geri bildirim',
  'ob.welcome.note': 'Birkaç soru sana uygun partnerleri bulmamıza yardım eder. Yaklaşık iki dakika sürer.',

  'ob.goal.title': 'Seni buraya ne getirdi?',
  'ob.goal.sub': 'Şu an senin için en önemli olanı seç.',
  'goal.Speaking': 'Rahat konuşmak',
  'goal.Speaking.hint': 'Günlük sohbette donup kalmadan',
  'goal.Business': 'İş için İngilizce',
  'goal.Business.hint': 'Toplantılar, aramalar, mülakatlar',
  'goal.Exam': 'Sınavı geçmek',
  'goal.Exam.hint': 'IELTS, TOEFL, okul sınavları',
  'goal.Travel': 'Seyahat ve yurt dışında yaşam',
  'goal.Travel.hint': 'Yol bulmak, insanlarla tanışmak',
  'goal.Fun': 'Kendim için',
  'goal.Fun.hint': 'Dili seviyorum',

  'ob.level.title': 'Bugün nasıl konuşuyorsun?',
  'ob.level.sub': 'Kiminle pratik yapacağını seviyen belirler. Sonra değiştirebilirsin.',
  'ob.level.test': 'Emin değil misin? 3 dakikalık seviye testini çöz',
  'level.A1': 'Başlangıç',
  'level.A1.hint': 'Birkaç kelime ve kalıp biliyorum',
  'level.A2': 'Temel',
  'level.A2.hint': 'Basit, tanıdık konularda konuşabiliyorum',
  'level.B1': 'Orta',
  'level.B1.hint': 'Duraksayarak da olsa sohbeti sürdürebiliyorum',
  'level.B2': 'Orta üstü',
  'level.B2.hint': 'Çoğu konuda oldukça rahat konuşuyorum',
  'level.C1': 'İleri',
  'level.C1.hint': 'Akıcı konuşuyorum, cilalamak istiyorum',
  'level.C2': 'Uzman',
  'level.C2.hint': 'Ana dile yakın',

  'ob.age.title': 'Kaç yaşındasın?',
  'ob.age.sub': 'Bunu sana uygun partner seçmek için kullanıyoruz. Yalnızca SpeakLab ekibi görür.',
  'age.under18': '18 yaş altı',
  'age.35+': '35 ve üzeri',

  'ob.place.title': 'Neredesin?',
  'ob.place.sub': 'Partnerlerin başka bir ülkede olabilir, bu yüzden her pratiği senin saatine göre planlıyoruz.',
  'ob.place.now': 'Şu an senin saatin:',
  'ob.place.change': 'Değiştir',
  'ob.place.done': 'Tamam',
  'ob.place.note': 'Yukarıdaki saat şu anki saatin değilse «Değiştir»e dokun.',
  'country.Azerbaijan': 'Azerbaycan',
  'country.Türkiye': 'Türkiye',
  'country.Georgia': 'Gürcistan',
  'country.Russia': 'Rusya',
  'country.Kazakhstan': 'Kazakistan',
  'country.Uzbekistan': 'Özbekistan',
  'country.Germany': 'Almanya',
  'country.United Kingdom': 'Birleşik Krallık',
  'country.United States': 'ABD',
  'country.Other': 'Diğer',

  'ob.charter.title': 'Burada pratik nasıl işler',
  'ob.charter.sub': 'SpeakLab’de her pratik önceden, gerçek bir partnerle planlanır.',
  'ob.charter.c1': 'Gerçekten katılabileceğin saatleri ve haftada kaç pratik istediğini bize söylersin.',
  'ob.charter.c2': 'Her pazar akşamı gelecek haftanın pratikleri gelir. Her birini tek dokunuşla onayla — partnerin de «evet» deyince ayrılır.',
  'ob.charter.c3': 'Partnerin o saati senin için ayırır, sen de onun için. Planın mı değişti? Saat başkasına geçebilsin diye en az 2 saat önce iptal et.',
  'ob.charter.c4': 'Pratik yaptığın herkese istediğin zaman yazabilir ya da arayabilirsin.',

  'ob.phone.title': 'WhatsApp numaran',
  'ob.phone.sub': 'Yalnızca SpeakLab ekibi görür — bir pratikle ilgili bir şey çözmek gerektiğinde yazarız. Partnerler uygulama içi sohbette konuşur.',
  'ob.phone.ok': '{n} numarasına yazacağız',
  'ob.phone.short': 'Bu henüz tam bir numaraya benzemiyor.',
  'ob.phone.hint': 'WhatsApp’ta kullandığın numara.',
  'ob.phone.required': 'Kaydetmek için WhatsApp numaranı ekle.',
  'ob.notify.title': 'Partnerin bulununca haberin olsun',
  'ob.notify.sub': 'Sana bir pratik ayarlandığında bir mesaj, başlamadan önce de bir mesaj göndeririz.',
  'ob.notify.on': 'Bildirimleri aç',
  'ob.notify.later': 'Şimdi değil',
  'ob.notify.done': 'Bildirimler açık.',
  'ob.notify.blocked': 'Bu tarayıcıda bildirimler kapalı — sonra ayarlarından açabilirsin.',

  'ob.avail.title': 'Genelde ne zaman boşsun?',
  'ob.avail.sub': 'Boş olduğun saatlere dokun. Saat: {city}.',
  'ob.avail.clear': 'Temizle',
  'ob.avail.popular': 'Popüler — bu saatlerde daha çok kişi boş',
  'ob.avail.editing': 'Değişiklikler bir sonraki haftalık plandan (pazar) itibaren geçerli. Onayladığın pratikler olduğu gibi kalır.',
  'ob.avail.none': 'En az bir boş saat seç.',
  'ob.avail.summary.one': '{d} günde {h} saat boş',
  'ob.avail.summary.other': '{d} günde {h} saat boş',
  'ob.avail.maybe': '{n} saat «belki»',
  'ob.avail.never': '{n} saat «asla»',
  'preset.weekday-evenings': 'Hafta içi akşamları',
  'preset.weekend-days': 'Hafta sonu gündüz',
  'preset.late': 'Geç akşamlar',
  'mode.free': 'Boş',
  'mode.free.hint': 'Bu yeterli',
  'mode.maybe': 'Belki',
  'mode.maybe.hint': 'İsteğe bağlı',
  'mode.never': 'Asla',
  'mode.never.hint': 'İsteğe bağlı',
  'ob.avail.howMaybe': 'Belki: başka saat olmazsa.',
  'ob.avail.howNever': 'Asla: bu saatleri önermeyiz.',
  'ob.avail.backFree': '«Boş»a dön',

  'ob.target.title': 'Haftada kaç pratiğe katılacaksın?',
  'ob.target.sub': 'Boş olmak söz vermekle aynı şey değil. Gerçekten katılabileceğin sayıyı seç — onaylanan her pratikte seni bir partner bekler.',
  'target.1': 'Haftada bir',
  'target.2': 'Haftada iki',
  'target.3': 'Üç kez',
  'target.4': 'Dört veya daha fazla',
  'ob.target.limit.one': '{d} gün boşsun, bu yüzden haftada en fazla {d} pratik planlayabilirsin.',
  'ob.target.limit.other': '{d} gün boşsun, bu yüzden haftada en fazla {d} pratik planlayabilirsin.',
  'ob.target.addMore': 'Daha fazla boş zaman ekle',

  'topic.Technology': 'Teknoloji',
  'topic.Movies': 'Filmler',
  'topic.Music': 'Müzik',
  'topic.Travel': 'Seyahat',
  'topic.Business': 'İş dünyası',
  'topic.Sport': 'Spor',
  'topic.Food': 'Yemek',
  'topic.Books': 'Kitaplar',
  'topic.Science': 'Bilim',
  'topic.Psychology': 'Psikoloji',
  'topic.Career': 'Kariyer',
  'topic.General': 'Genel',

  'ob.summary.title': 'Pratik profilin',
  'ob.summary.sub': 'Bir kez kontrol et. SpeakLab ekibi partnerlerini ve pratik saatlerini buna göre planlar.',
  'row.goal': 'Hedef',
  'row.level': 'Seviye',
  'row.age': 'Yaş',
  'row.location': 'Konum',
  'row.whatsapp': 'WhatsApp',
  'row.free': 'Boş zaman',
  'row.perWeek': 'Haftada',
  'row.maybe': 'Belki: {n} saat',
  'row.never': 'Asla: {n} saat',
  'row.perWeekValue': '{n} pratik',
  'day.0': 'Paz',
  'day.1': 'Pzt',
  'day.2': 'Sal',
  'day.3': 'Çar',
  'day.4': 'Per',
  'day.5': 'Cum',
  'day.6': 'Cmt',

  'intro.top': 'Ekiple tanış',
  'intro.back': 'Uygulamaya dön',
  'intro.doneTitle': 'Ekiple tanıştın',
  'intro.doneSub': 'Sorun mu var? WhatsApp’tayız.',
  'intro.title': 'SpeakLab ekibine merhaba de',
  'intro.subMust': 'İlk adımın: bize WhatsApp’tan yaz. Hedeflerini anlat, istediğini sor; pratik haftanı planlamana yardım edelim. Sonra tüm uygulama, aramalar dahil, senin.',
  'intro.sub': 'Bize WhatsApp’tan yaz: istediğini sor, hedeflerini anlat; pratik haftanı planlamana yardım edelim. Tüm uygulama şu an sana açık.',
  'intro.wa': 'Bize WhatsApp’tan yaz',
  'intro.waSub': 'Adın ve seviyen mesajda zaten yazılı',
  'intro.continue': 'Uygulamaya geç',
  'intro.waText': 'Merhaba! Ben {name}{level}. SpeakLab’e yeni katıldım.',

  'tour.badge': 'Kısa rehber',
  'tour.skip': 'Geç',
  'tour.back': 'Geri',
  'tour.next': 'İleri',
  'tour.finish': 'Bitir',
  'tour.next.title': 'Pratiklerin planlanır',
  'tour.next.text': 'Her hafta senin için gerçek partnerlerle pratikler planlıyoruz. Plan sekmesinde her birini tek dokunuşla onayla.',
  'tour.topic.title': 'Bugünün konusu',
  'tour.topic.text': 'Bugünkü sohbet için kelimeler, sorular ve kısa bir dinleme. Konuşmadan önce aç.',
  'tour.ai.title': 'AInur, her an',
  'tour.ai.text': 'İstediğin zaman AInur ile konuşarak ısın.',
  'tour.lab.title': 'Gelişimin',
  'tour.lab.text': 'Her pratikten sonra Lab yeni kelimelerini, hatalarını ve nasıl geliştiğini gösterir.',
  'tour.board.title': 'Liderlik tablosu',
  'tour.board.text': 'Bu hafta en çok kimin pratik yaptığını gör. Her pratik seni yukarı taşır.',
  'tour.partners.title': 'Partnerler',
  'tour.partners.text': 'Pratik yaptığın kişilere yaz ve onları yeniden ara.',
  // The install gate (components/InstallGate.jsx) — the browser steps stay English.
  'gate.play.title': 'SpeakLab uygulamasını indir',
  'gate.play.sub': 'Google Play’de. Partnerinin araması ekran kapalıyken bile çalar, pratik hatırlatmaları sana ulaşır.',
  'gate.perk.calls': 'Aramalar çalar',
  'gate.perk.reminders': 'Hatırlatmalar',
  'gate.perk.updates': 'Kendini günceller',
  'gate.perk.full': 'Tam ekran',
  'gate.play.button': 'Google Play’den indir',
  'gate.play.after': 'Sonra burada olduğu gibi giriş yap — hesabın ve pratiklerin zaten orada.',
  'gate.skip': 'Tarayıcıda devam et',
  'gate.a2hs.title': 'SpeakLab’i ana ekrana ekle',
  'gate.inapp.title': 'SpeakLab’i tarayıcında aç',
  'gate.a2hs.sub': 'Uygulama gibi tam ekran açılır, partnerinin aramaları ve pratik hatırlatmaları sana ulaşır.',
  'gate.install': 'SpeakLab’i yükle',
  'gate.in': 'Tarayıcı:',
  'gate.byhand': 'Ya da elle:',
  'gate.other': 'Başka tarayıcı',
  'gate.copy': 'Bağlantıyı kopyala',
  'gate.copied': 'Kopyalandı',
  'gate.after': 'Sonra SpeakLab’i ana ekrandan aç ve bildirimlere izin ver.',
  'gate.help': 'Yardım mı lazım? Bize yaz',
};

const ru = {
  'lang.pick': 'Язык',

  'reg.title': 'Начни {говорить}',
  'reg.sub': 'Говори каждый день — с живым партнёром или с AInur.',
  'reg.invite': 'Ты присоединяешься по приглашению преподавателя — сразу после регистрации тебя подключат к нему.',
  'reg.student': 'Я ученик',
  'reg.studentSub': 'Практика речи',
  'reg.teacher': 'Я преподаватель',
  'reg.teacherSub': 'Следить за учениками',
  'reg.chooseRole': 'Выбери роль, чтобы продолжить',
  'reg.google': 'Регистрация через Google',
  'reg.or': 'или',
  'reg.name': 'Имя и фамилия',
  'reg.namePh': 'Твоё имя',
  'reg.email': 'Эл. почта',
  'reg.password': 'Пароль',
  'reg.passwordPh': 'Минимум 6 символов',
  'reg.submit': 'Начать',
  'reg.creating': 'Создаём аккаунт...',
  'reg.haveAccount': 'Уже есть аккаунт?',
  'reg.signIn': 'Войти',
  'reg.googleError': 'Регистрация через Google не удалась: {msg}',

  'ob.back': 'Назад',
  'ob.continue': 'Дальше',
  'ob.getStarted': 'Начнём',
  'ob.imIn': 'Я в деле',
  'ob.save': 'Сохранить',
  'ob.saving': 'Сохраняем…',
  'ob.confirm': 'Подтвердить и спланировать неделю',
  'ob.reviewAll': 'Посмотреть все ответы',
  'ob.continueTarget': 'Дальше — выбери цель на неделю',
  'ob.errSave': 'Ответы не сохранились. Проверь интернет и попробуй ещё раз.',
  'ob.edit': 'Изменить',

  'ob.welcome.title': 'Место, где ты говоришь по-английски каждую неделю',
  'ob.welcome.sub': 'Ты практикуешься с живыми партнёрами в удобное время, а после каждого разговора получаешь отчёт: над чем работать дальше.',
  'ob.welcome.p1': 'Живые разговоры с людьми твоего уровня',
  'ob.welcome.p2': 'Недельный план под твоё свободное время',
  'ob.welcome.p3': 'Разбор после каждой практики',
  'ob.welcome.note': 'Несколько вопросов помогут подобрать тебе партнёров. Это займёт около двух минут.',

  'ob.goal.title': 'Зачем ты здесь?',
  'ob.goal.sub': 'Выбери то, что сейчас важнее всего.',
  'goal.Speaking': 'Говорить уверенно',
  'goal.Speaking.hint': 'Обычный разговор без ступора',
  'goal.Business': 'Английский для работы',
  'goal.Business.hint': 'Встречи, звонки, собеседования',
  'goal.Exam': 'Сдать экзамен',
  'goal.Exam.hint': 'IELTS, TOEFL, школьные экзамены',
  'goal.Travel': 'Путешествия и жизнь за границей',
  'goal.Travel.hint': 'Ориентироваться, знакомиться',
  'goal.Fun': 'Для себя',
  'goal.Fun.hint': 'Мне нравится язык',

  'ob.level.title': 'Как ты говоришь сейчас?',
  'ob.level.sub': 'От уровня зависит, с кем ты будешь практиковаться. Его можно поменять позже.',
  'ob.level.test': 'Не уверен(а)? Пройди тест на уровень за 3 минуты',
  'level.A1': 'Начальный',
  'level.A1.hint': 'Знаю немного слов и фраз',
  'level.A2': 'Элементарный',
  'level.A2.hint': 'Могу говорить о простых, знакомых вещах',
  'level.B1': 'Средний',
  'level.B1.hint': 'Могу поддерживать разговор, с паузами',
  'level.B2': 'Выше среднего',
  'level.B2.hint': 'Довольно свободно говорю на большинство тем',
  'level.C1': 'Продвинутый',
  'level.C1.hint': 'Говорю бегло, хочу отшлифовать',
  'level.C2': 'В совершенстве',
  'level.C2.hint': 'Почти как носитель',

  'ob.age.title': 'Сколько тебе лет?',
  'ob.age.sub': 'Это нужно, чтобы подобрать подходящих партнёров. Видит только команда SpeakLab.',
  'age.under18': 'Младше 18',
  'age.35+': '35 и старше',

  'ob.place.title': 'Где ты находишься?',
  'ob.place.sub': 'Партнёры могут быть в другой стране, поэтому каждую практику мы планируем по твоему времени.',
  'ob.place.now': 'Сейчас у тебя:',
  'ob.place.change': 'Изменить',
  'ob.place.done': 'Готово',
  'ob.place.note': 'Если время выше не совпадает с твоими часами, нажми «Изменить».',
  'country.Azerbaijan': 'Азербайджан',
  'country.Türkiye': 'Турция',
  'country.Georgia': 'Грузия',
  'country.Russia': 'Россия',
  'country.Kazakhstan': 'Казахстан',
  'country.Uzbekistan': 'Узбекистан',
  'country.Germany': 'Германия',
  'country.United Kingdom': 'Великобритания',
  'country.United States': 'США',
  'country.Other': 'Другая',

  'ob.charter.title': 'Как здесь устроена практика',
  'ob.charter.sub': 'Каждая практика в SpeakLab планируется заранее, с живым партнёром.',
  'ob.charter.c1': 'Ты говоришь нам часы, в которые точно сможешь, и сколько практик в неделю хочешь.',
  'ob.charter.c2': 'Каждое воскресенье вечером приходят практики на следующую неделю. Подтверди каждую одним касанием — она закреплена, когда партнёр тоже скажет «да».',
  'ob.charter.c3': 'Партнёр держит этот час для тебя, а ты — для него. Планы поменялись? Отмени минимум за 2 часа, чтобы время досталось кому-то ещё.',
  'ob.charter.c4': 'С каждым, с кем ты практиковался, можно в любое время переписываться или созвониться.',

  'ob.phone.title': 'Твой номер WhatsApp',
  'ob.phone.sub': 'Видит только команда SpeakLab — мы пишем, когда с практикой нужно что-то решить. Партнёры общаются в чате приложения.',
  'ob.phone.ok': 'Напишем на {n}',
  'ob.phone.short': 'Пока не похоже на полный номер.',
  'ob.phone.hint': 'Номер, который ты используешь в WhatsApp.',
  'ob.phone.required': 'Добавь номер WhatsApp, чтобы сохранить.',
  'ob.notify.title': 'Узнай, когда найдётся партнёр',
  'ob.notify.sub': 'Пришлём сообщение, когда тебе назначат практику, и ещё одно перед её началом.',
  'ob.notify.on': 'Включить уведомления',
  'ob.notify.later': 'Не сейчас',
  'ob.notify.done': 'Уведомления включены.',
  'ob.notify.blocked': 'В этом браузере уведомления выключены — позже их можно включить в настройках.',

  'ob.avail.title': 'Когда ты обычно свободен(на)?',
  'ob.avail.sub': 'Отметь свободные часы. Время: {city}.',
  'ob.avail.clear': 'Очистить',
  'ob.avail.popular': 'Популярное — в это время свободно больше людей',
  'ob.avail.editing': 'Изменения действуют со следующего недельного плана (воскресенье). Уже подтверждённые практики остаются как есть.',
  'ob.avail.none': 'Выбери хотя бы один свободный час.',
  'ob.avail.summary.one': '{h} ч свободно · дней: {d}',
  'ob.avail.summary.other': '{h} ч свободно · дней: {d}',
  'ob.avail.maybe': '{n} ч «может быть»',
  'ob.avail.never': '{n} ч «никогда»',
  'preset.weekday-evenings': 'Будни вечером',
  'preset.weekend-days': 'Выходные днём',
  'preset.late': 'Поздно вечером',
  'mode.free': 'Свободно',
  'mode.free.hint': 'Этого хватит',
  'mode.maybe': 'Может',
  'mode.maybe.hint': 'По желанию',
  'mode.never': 'Никогда',
  'mode.never.hint': 'По желанию',
  'ob.avail.howMaybe': 'Может: если нет другого времени.',
  'ob.avail.howNever': 'Никогда: эти часы не предложим.',
  'ob.avail.backFree': 'Назад к «Свободно»',

  'ob.target.title': 'Сколько практик в неделю ты будешь проводить?',
  'ob.target.sub': 'Быть свободным — не то же самое, что пообещать. Выбери число, которое реально выдержишь: на каждой подтверждённой практике тебя ждёт партнёр.',
  'target.1': 'Раз в неделю',
  'target.2': 'Два раза',
  'target.3': 'Три раза',
  'target.4': 'Четыре и больше',
  'ob.target.limit.one': 'Свободных дней: {d}, поэтому можно запланировать не больше {d} в неделю.',
  'ob.target.limit.other': 'Свободных дней: {d}, поэтому можно запланировать не больше {d} в неделю.',
  'ob.target.addMore': 'Добавить свободное время',

  'topic.Technology': 'Технологии',
  'topic.Movies': 'Кино',
  'topic.Music': 'Музыка',
  'topic.Travel': 'Путешествия',
  'topic.Business': 'Бизнес',
  'topic.Sport': 'Спорт',
  'topic.Food': 'Еда',
  'topic.Books': 'Книги',
  'topic.Science': 'Наука',
  'topic.Psychology': 'Психология',
  'topic.Career': 'Карьера',
  'topic.General': 'Общее',

  'ob.summary.title': 'Твой профиль практики',
  'ob.summary.sub': 'Проверь один раз. По нему команда SpeakLab планирует твоих партнёров и время практик.',
  'row.goal': 'Цель',
  'row.level': 'Уровень',
  'row.age': 'Возраст',
  'row.location': 'Место',
  'row.whatsapp': 'WhatsApp',
  'row.free': 'Свободное время',
  'row.perWeek': 'В неделю',
  'row.maybe': 'Может быть: {n} ч',
  'row.never': 'Никогда: {n} ч',
  'row.perWeekValue': '{n}',
  'day.0': 'Вс',
  'day.1': 'Пн',
  'day.2': 'Вт',
  'day.3': 'Ср',
  'day.4': 'Чт',
  'day.5': 'Пт',
  'day.6': 'Сб',

  'intro.top': 'Познакомься с командой',
  'intro.back': 'Вернуться в приложение',
  'intro.doneTitle': 'Ты познакомился(ась) с командой',
  'intro.doneSub': 'Есть вопросы? Мы в WhatsApp.',
  'intro.title': 'Напиши привет команде SpeakLab',
  'intro.subMust': 'Твой первый шаг: напиши нам в WhatsApp. Расскажи о целях, спроси что угодно — мы поможем спланировать неделю практики. После этого всё приложение, включая звонки, твоё.',
  'intro.sub': 'Напиши нам в WhatsApp: спроси что угодно, расскажи о целях — мы поможем спланировать неделю практики. Всё приложение уже открыто.',
  'intro.wa': 'Написать нам в WhatsApp',
  'intro.waSub': 'Имя и уровень уже есть в сообщении',
  'intro.continue': 'Перейти в приложение',
  'intro.waText': 'Привет! Я {name}{level}. Только что присоединился(ась) к SpeakLab.',

  'tour.badge': 'Короткий гид',
  'tour.skip': 'Пропустить',
  'tour.back': 'Назад',
  'tour.next': 'Дальше',
  'tour.finish': 'Готово',
  'tour.next.title': 'Практики по плану',
  'tour.next.text': 'Каждую неделю мы планируем для тебя практики с реальными партнёрами. Во вкладке Plan каждую подтверждаешь одним касанием.',
  'tour.topic.title': 'Тема дня',
  'tour.topic.text': 'Слова, вопросы и короткое аудио для сегодняшнего разговора. Открой перед разговором.',
  'tour.ai.title': 'AInur в любое время',
  'tour.ai.text': 'Разогрейся, поговорив с AInur, когда захочешь.',
  'tour.lab.title': 'Твой прогресс',
  'tour.lab.text': 'После каждой практики Lab показывает новые слова, ошибки и твой рост.',
  'tour.board.title': 'Таблица лидеров',
  'tour.board.text': 'Смотри, кто больше всех практиковался на этой неделе. Каждая практика поднимает тебя выше.',
  'tour.partners.title': 'Партнёры',
  'tour.partners.text': 'Пиши тем, с кем практиковался, и звони им снова.',
  // The install gate (components/InstallGate.jsx) — the browser steps stay English.
  'gate.play.title': 'Скачай приложение SpeakLab',
  'gate.play.sub': 'Оно в Google Play. Звонки партнёра звучат даже при выключенном экране, а напоминания о практике до тебя доходят.',
  'gate.perk.calls': 'Звонки',
  'gate.perk.reminders': 'Напоминания',
  'gate.perk.updates': 'Само обновляется',
  'gate.perk.full': 'Полный экран',
  'gate.play.button': 'Скачать в Google Play',
  'gate.play.after': 'Потом войди так же, как здесь, — твой аккаунт и практики уже там.',
  'gate.skip': 'Продолжить в браузере',
  'gate.a2hs.title': 'Добавь SpeakLab на главный экран',
  'gate.inapp.title': 'Открой SpeakLab в браузере',
  'gate.a2hs.sub': 'Он открывается на весь экран, как приложение, а звонки партнёра и напоминания до тебя доходят.',
  'gate.install': 'Установить SpeakLab',
  'gate.in': 'Браузер:',
  'gate.byhand': 'Или вручную:',
  'gate.other': 'Другой браузер',
  'gate.copy': 'Скопировать ссылку',
  'gate.copied': 'Скопировано',
  'gate.after': 'Потом открой SpeakLab с главного экрана и разреши уведомления.',
  'gate.help': 'Нужна помощь? Напиши нам',
};

export const FIRST_RUN_COPY = { en, az, tr, ru };

// t('ob.avail.sub', { city: 'Baku' }) in `lang`, English when the key is missing.
export function firstRunText(lang, key, vars) {
  const raw = (FIRST_RUN_COPY[lang] && FIRST_RUN_COPY[lang][key]) ?? en[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
}
